import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Warehouse, ChevronDown, Package, Eye, X,
  ArrowDownAZ, ArrowUpAZ, ArrowDown01, ArrowUp01, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Sort options ── */
const SORT_OPTIONS = [
  { key: "oldest",      label: "Oldest Arrival",   icon: CalendarClock },
  { key: "newest",      label: "Newest Arrival",   icon: CalendarClock },
  { key: "max_items",   label: "Maximum Items",    icon: ArrowDown01   },
  { key: "min_items",   label: "Minimum Items",    icon: ArrowUp01     },
  { key: "alpha",       label: "Alphabetical",     icon: ArrowDownAZ   },
];

function sortCustomers(customers, sortKey) {
  const c = [...customers];
  switch (sortKey) {
    case "oldest":
      return c.sort((a, b) => {
        const da = a.arrival_dates[0] || "";
        const db_ = b.arrival_dates[0] || "";
        return da.localeCompare(db_);
      });
    case "newest":
      return c.sort((a, b) => {
        const da = a.arrival_dates[a.arrival_dates.length - 1] || "";
        const db_ = b.arrival_dates[b.arrival_dates.length - 1] || "";
        return db_.localeCompare(da);
      });
    case "max_items":
      return c.sort((a, b) => b.total_items - a.total_items);
    case "min_items":
      return c.sort((a, b) => a.total_items - b.total_items);
    case "alpha":
      return c.sort((a, b) => a.customer_name.localeCompare(b.customer_name));
    default:
      return c;
  }
}

function formatArrival(dates) {
  if (!dates || dates.length === 0) return "—";
  if (dates.length === 1) {
    try { return format(new Date(dates[0] + "T00:00:00"), "dd MMM yyyy"); }
    catch { return dates[0]; }
  }
  return "Arrived on multiple dates";
}

/* ── Load detail dialog ── */
function LoadDetailDialog({ open, onClose, customer, warehouseName }) {
  if (!customer) return null;
  const { customer_name, loads } = customer;

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none p-0 gap-0">
        {/* Header */}
        <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
          <DialogTitle className="text-white font-bold tracking-wide text-base">
            {customer_name} — Load Details
          </DialogTitle>
          <p className="text-white/60 text-xs mt-0.5">
            {loads.length} item{loads.length !== 1 ? "s" : ""} currently stored in{" "}
            <span className="text-[#FFD700] font-semibold">{warehouseName}</span>
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                {["Ref", "Item Name", "Description", "Weight", "Volume", "Arrived On"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loads.map((load, idx) => (
                <tr
                  key={load.load_id}
                  className={`border-b border-[#E2E8F0] ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-[#1B2A6B] font-bold whitespace-nowrap">
                    {load.load_ref || "—"}
                  </td>
                  <td className="px-4 py-3 font-semibold text-[#0F172A]">
                    {load.load_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-[#475569] max-w-[160px] truncate">
                    {load.description || "—"}
                  </td>
                  <td className="px-4 py-3 text-[#475569] whitespace-nowrap">
                    {load.weight != null ? `${load.weight} kg` : "—"}
                  </td>
                  <td className="px-4 py-3 text-[#475569] whitespace-nowrap">
                    {load.volume != null ? `${load.volume} m³` : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#475569] whitespace-nowrap">
                    {load.arrival_date
                      ? (() => { try { return format(new Date(load.arrival_date + "T00:00:00"), "dd MMM yyyy"); } catch { return load.arrival_date; } })()
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] flex justify-end">
          <Button
            onClick={onClose}
            className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700]"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main page ── */
export default function WarehouseReportPage() {
  const [warehouses, setWarehouses]           = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [report, setReport]                   = useState(null);
  const [loading, setLoading]                 = useState(false);
  const [sortKey, setSortKey]                 = useState("oldest");
  const [showWHDropdown, setShowWHDropdown]   = useState(false);
  const [viewCustomer, setViewCustomer]       = useState(null);

  /* Load warehouse list */
  useEffect(() => {
    axios.get(`${API}/warehouses`, { withCredentials: true })
      .then(r => setWarehouses(r.data))
      .catch(() => toast.error("Failed to load warehouses."));
  }, []);

  /* Fetch report when warehouse changes */
  const loadReport = async (wh) => {
    setSelectedWarehouse(wh);
    setShowWHDropdown(false);
    setReport(null);
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/reporting/warehouse/${encodeURIComponent(wh.warehouse_name)}`,
        { withCredentials: true }
      );
      setReport(data);
    } catch {
      toast.error("Failed to load warehouse report.");
    } finally {
      setLoading(false);
    }
  };

  /* Apply sort */
  const sortedCustomers = useMemo(() => {
    if (!report) return [];
    return sortCustomers(report.customers, sortKey);
  }, [report, sortKey]);

  return (
    <div className="space-y-5" data-testid="warehouse-report-page">

      {/* Page header */}
      <div>
        <h2 className="text-xl font-bold text-[#1B2A6B]">Warehouse Report</h2>
        <p className="text-xs text-[#475569] mt-0.5">View current stock in any warehouse</p>
      </div>

      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-3">

        {/* Warehouse selector */}
        <div className="relative">
          <button
            onClick={() => setShowWHDropdown(v => !v)}
            className="flex items-center gap-2 px-4 h-10 border border-[#E2E8F0] bg-white text-sm font-semibold text-[#0F172A] hover:border-[#1B2A6B] transition-colors min-w-[200px]"
          >
            <Warehouse size={15} className="text-[#1B2A6B] shrink-0" />
            <span className="flex-1 text-left truncate">
              {selectedWarehouse ? selectedWarehouse.warehouse_name : "Select Warehouse"}
            </span>
            <ChevronDown size={14} className="text-[#94A3B8] shrink-0" />
          </button>

          {showWHDropdown && (
            <div className="absolute top-full left-0 mt-1 w-full min-w-[240px] bg-white border border-[#E2E8F0] shadow-lg z-30">
              {warehouses.length === 0 ? (
                <p className="px-4 py-3 text-sm text-[#94A3B8]">No warehouses found.</p>
              ) : (
                warehouses.map(wh => (
                  <button
                    key={wh.id}
                    onClick={() => loadReport(wh)}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F1F5F9] transition-colors border-b border-[#F1F5F9] last:border-b-0 ${
                      selectedWarehouse?.id === wh.id ? "bg-[#EEF2FF] text-[#1B2A6B] font-semibold" : "text-[#0F172A]"
                    }`}
                  >
                    <p className="font-medium">{wh.warehouse_name}</p>
                    {wh.city_name && <p className="text-xs text-[#94A3B8]">{wh.city_name}</p>}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        {selectedWarehouse && (
          <div className="h-8 w-px bg-[#E2E8F0] hidden sm:block" />
        )}

        {/* Sort pills — only when warehouse is selected */}
        {selectedWarehouse && (
          <div className="flex flex-wrap items-center gap-1.5">
            {SORT_OPTIONS.map(opt => {
              const Icon = opt.icon;
              const active = sortKey === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setSortKey(opt.key)}
                  className={`flex items-center gap-1.5 px-3 h-8 text-[11px] font-bold uppercase tracking-widest transition-colors border ${
                    active
                      ? "bg-[#1B2A6B] text-white border-[#1B2A6B]"
                      : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1B2A6B] hover:text-[#1B2A6B]"
                  }`}
                >
                  <Icon size={12} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Click-away to close dropdown */}
      {showWHDropdown && (
        <div className="fixed inset-0 z-20" onClick={() => setShowWHDropdown(false)} />
      )}

      {/* State: no warehouse selected */}
      {!selectedWarehouse && !loading && (
        <div className="bg-white border border-[#E2E8F0] flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 border-2 border-dashed border-[#1B2A6B]/20 rounded-full flex items-center justify-center">
            <Warehouse size={28} className="text-[#CBD5E1]" />
          </div>
          <p className="text-sm text-[#94A3B8] font-medium">Select a warehouse above to view its current contents</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white border border-[#E2E8F0] flex items-center justify-center py-24 text-sm text-[#94A3B8]">
          Loading report...
        </div>
      )}

      {/* Report */}
      {report && !loading && (
        <>
          {/* Summary strip */}
          <div className="flex items-center gap-6 bg-[#1B2A6B] px-6 py-3 border-b-4 border-[#FFD700]">
            <div>
              <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Warehouse</p>
              <p className="text-white font-bold text-sm mt-0.5">{report.warehouse_name}</p>
            </div>
            <div className="w-px h-8 bg-white/15" />
            <div>
              <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Total Items</p>
              <p className="text-[#FFD700] font-bold text-sm mt-0.5">{report.total_items}</p>
            </div>
            <div className="w-px h-8 bg-white/15" />
            <div>
              <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Customers</p>
              <p className="text-white font-bold text-sm mt-0.5">{report.customers.length}</p>
            </div>
          </div>

          {/* Empty warehouse */}
          {sortedCustomers.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] flex flex-col items-center justify-center py-20 gap-3">
              <Package size={40} className="text-[#E2E8F0]" />
              <p className="text-sm text-[#94A3B8]">No items currently stored in this warehouse.</p>
            </div>
          ) : (
            /* Customer grid */
            <div className="bg-white border border-[#E2E8F0] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                    {["#", "Customer Name", "Total Items", "Arrival Date", ""].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedCustomers.map((cust, idx) => (
                    <tr
                      key={cust.booking_id}
                      className={`border-b border-[#E2E8F0] ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
                    >
                      <td className="px-4 py-3.5 text-[#94A3B8] text-xs font-mono">{idx + 1}</td>

                      <td className="px-4 py-3.5 font-semibold text-[#0F172A]">
                        {cust.customer_name}
                      </td>

                      <td className="px-4 py-3.5">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#EEF2FF] text-[#1B2A6B] text-xs font-bold rounded-sm border border-[#C7D2FE]">
                          <Package size={11} />
                          {cust.total_items} item{cust.total_items !== 1 ? "s" : ""}
                        </span>
                      </td>

                      <td className="px-4 py-3.5 text-sm text-[#475569]">
                        {cust.arrival_dates.length > 1 ? (
                          <span className="text-amber-600 font-medium text-xs">Arrived on multiple dates</span>
                        ) : (
                          formatArrival(cust.arrival_dates)
                        )}
                      </td>

                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => setViewCustomer(cust)}
                          className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#1B2A6B] hover:text-white border border-[#1B2A6B] hover:bg-[#1B2A6B] transition-colors px-3 py-1.5 rounded-sm"
                        >
                          <Eye size={12} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Load detail dialog */}
      <LoadDetailDialog
        open={!!viewCustomer}
        onClose={() => setViewCustomer(null)}
        customer={viewCustomer}
        warehouseName={selectedWarehouse?.warehouse_name}
      />
    </div>
  );
}
